import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Reply, ReplyAll, Forward, Star, Flag,
  Trash2, Archive, Paperclip, Download, Loader2,
  Bot, CheckSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ComposeSheet from "@/components/mail/ComposeSheet";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MailDetail() {
  const { emailId } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyMode, setReplyMode] = useState(null); // reply | reply_all | forward

  useEffect(() => {
    if (emailId) fetchEmail();
  }, [emailId]);

  const fetchEmail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mail/emails/${emailId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      setEmail(res.data.email);

      // Mark as read
      if (!res.data.email.is_read) {
        await axios.patch(`${API}/mail/emails/${emailId}/read?is_read=true`, {}, {
          headers: getHeaders(), withCredentials: true,
        });
      }
    } catch {
      toast.error("Email not found");
      navigate("/comms-centre/mail/inbox");
    }
    setLoading(false);
  };

  const handleStar = async () => {
    await axios.post(`${API}/mail/emails/${emailId}/star`, {}, {
      headers: getHeaders(), withCredentials: true,
    });
    fetchEmail();
  };

  const handleFlag = async () => {
    await axios.post(`${API}/mail/emails/${emailId}/flag`, {}, {
      headers: getHeaders(), withCredentials: true,
    });
    fetchEmail();
  };

  const handleDelete = async () => {
    await axios.delete(`${API}/mail/emails/${emailId}`, {
      headers: getHeaders(), withCredentials: true,
    });
    toast.success("Moved to trash");
    navigate("/comms-centre/mail/inbox");
  };

  const handleArchive = async () => {
    await axios.post(`${API}/mail/emails/${emailId}/move`, { target_folder: "archive" }, {
      headers: getHeaders(), withCredentials: true,
    });
    toast.success("Archived");
    navigate("/comms-centre/mail/inbox");
  };

  const handleSummarize = async () => {
    try {
      const res = await axios.post(`${API}/mail/agent/summarize?email_id=${emailId}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(res.data.summary, { duration: 6000 });
    } catch {
      toast.error("Failed to summarize");
    }
  };

  const handleExtractTasks = async () => {
    try {
      const res = await axios.post(`${API}/mail/agent/extract-tasks?email_id=${emailId}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      const tasks = res.data.tasks || [];
      if (tasks.length === 0) {
        toast.info("No tasks found in this email");
        return;
      }
      toast.success(`Extracted ${tasks.length} tasks`, { duration: 4000 });
    } catch {
      toast.error("Failed to extract tasks");
    }
  };

  const openReply = (mode) => {
    setReplyMode(mode);
    setReplyOpen(true);
  };

  const getReplyData = () => {
    if (!email) return {};
    switch (replyMode) {
      case "reply":
        return {
          to: [email.from].filter(Boolean),
          subject: email.subject?.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
          body: `\n\n--- Original Message ---\nFrom: ${email.from?.name}\nDate: ${email.sent_at}\n\n${email.body}`,
          thread_id: email.thread_id,
          in_reply_to: email.id,
        };
      case "reply_all":
        return {
          to: [email.from, ...(email.to || [])].filter((r) => r.user_id !== email.from?.user_id),
          cc: email.cc || [],
          subject: email.subject?.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
          body: `\n\n--- Original Message ---\nFrom: ${email.from?.name}\nDate: ${email.sent_at}\n\n${email.body}`,
          thread_id: email.thread_id,
          in_reply_to: email.id,
        };
      case "forward":
        return {
          to: [],
          subject: email.subject?.startsWith("Fwd:") ? email.subject : `Fwd: ${email.subject}`,
          body: `\n\n--- Forwarded Message ---\nFrom: ${email.from?.name}\nDate: ${email.sent_at}\nSubject: ${email.subject}\nTo: ${(email.to || []).map((r) => r.name).join(", ")}\n\n${email.body}`,
          thread_id: email.thread_id,
          isForward: true,
        };
      default:
        return {};
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!email) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-app bg-app-card">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-7 w-7 p-0">
            <ArrowLeft className="w-4 h-4 text-app-secondary" />
          </Button>
          <Separator orientation="vertical" className="h-4 mx-1" />
          <Button variant="ghost" size="sm" onClick={() => openReply("reply")} className="h-7 text-xs px-2 gap-1">
            <Reply className="w-3 h-3" /> Reply
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openReply("reply_all")} className="h-7 text-xs px-2 gap-1">
            <ReplyAll className="w-3 h-3" /> Reply All
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openReply("forward")} className="h-7 text-xs px-2 gap-1">
            <Forward className="w-3 h-3" /> Forward
          </Button>
          <Separator orientation="vertical" className="h-4 mx-1" />
          <Button variant="ghost" size="sm" onClick={handleStar} className="h-7 text-xs px-2">
            <Star className="w-3 h-3" fill={email.is_starred ? "currentColor" : "none"} />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleFlag} className="h-7 text-xs px-2">
            <Flag className="w-3 h-3" fill={email.is_flagged ? "currentColor" : "none"} />
          </Button>
          <Separator orientation="vertical" className="h-4 mx-1" />
          <Button variant="ghost" size="sm" onClick={handleArchive} className="h-7 text-xs px-2">
            <Archive className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="h-7 text-xs px-2 text-red-500">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleSummarize} className="h-7 text-xs px-2 gap-1">
            <Bot className="w-3 h-3" /> Summarize
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExtractTasks} className="h-7 text-xs px-2 gap-1">
            <CheckSquare className="w-3 h-3" /> Tasks
          </Button>
        </div>
      </div>

      {/* Email content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl">
          {/* Subject */}
          <h1 className="text-lg font-semibold text-app mb-4">
            {email.subject || "(No subject)"}
          </h1>

          {/* From / To / Date */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-violet-800/40 flex items-center justify-center text-sm font-semibold text-violet-300 flex-shrink-0">
              {(email.from?.name || email.from?.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-app">{email.from?.name || email.from?.email}</span>
                {!email.is_read && (
                  <Badge className="text-[10px] px-1 py-0 bg-blue-800/40 text-blue-300 border-none">New</Badge>
                )}
              </div>
              <p className="text-xs text-app-secondary">{email.from?.email}</p>
              <div className="mt-1 text-xs text-app-secondary">
                <span>To: {(email.to || []).map((r) => r.name || r.email).join(", ") || "—"}</span>
                {email.cc?.length > 0 && (
                  <span className="ml-2">CC: {email.cc.map((r) => r.name || r.email).join(", ")}</span>
                )}
              </div>
              <p className="text-xs text-app-secondary mt-0.5">
                {email.sent_at ? new Date(email.sent_at).toLocaleString("en-ZA") : ""}
              </p>
            </div>
          </div>

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div className="mb-4 p-3 bg-app border border-app rounded">
              <p className="text-xs font-medium text-app-secondary mb-2 flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments ({email.attachments.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {email.attachments.map((att) => (
                  <a
                    key={att.id}
                    href={`${API}/mail/attachments/${att.id}?email_id=${email.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-2 py-1 bg-app-card border border-app rounded text-xs text-app hover:border-violet-300 hover:text-violet-600"
                  >
                    <Download className="w-3 h-3" />
                    {att.filename}
                    <span className="text-app-secondary">({Math.round(att.size / 1024)} KB)</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="text-sm text-app leading-relaxed whitespace-pre-wrap">
            {email.body || "(No content)"}
          </div>
        </div>
      </div>

      {/* Reply Sheet */}
      <Sheet open={replyOpen} onOpenChange={setReplyOpen}>
        <SheetContent side="right" className="w-[600px] sm:max-w-[600px] p-0">
          <ComposeSheet
            onClose={() => setReplyOpen(false)}
            onSent={() => { setReplyOpen(false); fetchEmail(); }}
            replyData={getReplyData()}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
